/**
 * AgentModelsPanel - per-tenant model tier for the master orchestrator and each sub-agent
 */

import { useEffect, useMemo, useState } from 'react';
import { Info, RotateCcw } from 'lucide-react';
import {
  AI_MODEL_TIERS,
  AgentModelLabels,
  type AgentModelKey,
  type AiModel,
  type TenantAgentConfigSetting,
  type UpdateInternalTenantAgentModelsRequest,
} from '@resolve-ai/shared';
import { LoadingState } from '../common';

const DEFAULT_OPTION = 'default';

type DraftValue = AiModel | typeof DEFAULT_OPTION;
type Draft = Partial<Record<AgentModelKey, DraftValue>>;

interface AgentModelsPanelProps {
  agentModels: TenantAgentConfigSetting[];
  isLoading: boolean;
  hasError: boolean;
  isSaving: boolean;
  onSave: (models: UpdateInternalTenantAgentModelsRequest['models']) => void;
}

function formatTier(tier: AiModel): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function toDraft(agentModels: TenantAgentConfigSetting[]): Draft {
  return Object.fromEntries(
    agentModels.map((setting) => [setting.agentKey, setting.isOverride ? setting.model : DEFAULT_OPTION])
  );
}

/**
 * Only agents whose selection changed - "default" becomes null (reset override)
 */
function toChangedModels(saved: Draft, draft: Draft): UpdateInternalTenantAgentModelsRequest['models'] {
  const changed: UpdateInternalTenantAgentModelsRequest['models'] = {};

  for (const [agentKey, value] of Object.entries(draft) as [AgentModelKey, DraftValue][]) {
    if (saved[agentKey] === value) continue;

    changed[agentKey] = value === DEFAULT_OPTION ? null : value;
  }

  return changed;
}

export function AgentModelsPanel({ agentModels, isLoading, hasError, isSaving, onSave }: AgentModelsPanelProps) {
  // Keyed on the saved values themselves, not the array reference, so a refetch
  // that returns the same data never wipes the admin's unsaved selections
  const savedSignature = JSON.stringify(toDraft(agentModels));
  const saved = useMemo<Draft>(() => JSON.parse(savedSignature), [savedSignature]);
  const [draft, setDraft] = useState<Draft>(saved);

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const changedModels = toChangedModels(saved, draft);
  const hasChanges = Object.keys(changedModels).length > 0;

  const setAgentDraft = (agentKey: AgentModelKey, value: DraftValue) =>
    setDraft((current) => ({ ...current, [agentKey]: value }));

  const resetAllToDefaults = () =>
    setDraft(Object.fromEntries(agentModels.map((setting) => [setting.agentKey, DEFAULT_OPTION])));

  if (hasError) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-800 dark:text-red-200">Failed to load AI models</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LoadingState isLoading={isLoading} message="Loading AI models..." />

      {!isLoading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Models</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Choose the Claude model tier each agent uses for this tenant.
              </p>
            </div>
            <button
              onClick={resetAllToDefaults}
              disabled={isSaving}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-all disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              Reset all to defaults
            </button>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {agentModels.map((setting) => {
              const value = draft[setting.agentKey] ?? DEFAULT_OPTION;
              const isCustomized = value !== DEFAULT_OPTION;

              return (
                <div key={setting.agentKey} className="flex items-center justify-between gap-4 py-3">
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor={`agent-model-${setting.agentKey}`}
                      className="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {AgentModelLabels[setting.agentKey]}
                    </label>
                    {isCustomized && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                        Customized
                      </span>
                    )}
                  </div>
                  <select
                    id={`agent-model-${setting.agentKey}`}
                    value={value}
                    onChange={(e) => setAgentDraft(setting.agentKey, e.target.value as DraftValue)}
                    disabled={isSaving}
                    className="w-48 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
                  >
                    <option value={DEFAULT_OPTION}>Default ({formatTier(setting.defaultModel)})</option>
                    {AI_MODEL_TIERS.map((tier) => (
                      <option key={tier} value={tier}>
                        {formatTier(tier)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-4 text-sm text-gray-600 dark:text-gray-400">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>Changes apply to new tasks. Running tasks are not affected.</span>
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={() => onSave(changedModels)}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all disabled:opacity-50"
            >
              Save Changes
            </button>
            <button
              onClick={() => setDraft(saved)}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
